import { inject, Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { Auth, PhoneAuthProvider, RecaptchaVerifier, signInWithCredential, updatePhoneNumber, updateProfile } from '@angular/fire/auth';
import { Timestamp } from '@angular/fire/firestore';
import { IUserPhoneSignUp } from '@auth/models/signup';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { AnalyticEvent } from '@shared/models/analytics';
import { IMobileNumber } from '@shared/models/numbers';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';
import { DEFAULT_USER_SETTINGS, IUser } from '@user/models/user';
import { UsersService } from '@user/services/users/users.service';

import { AuthToastService } from '../auth-toast/auth-toast.service';

@Injectable({ providedIn: 'root' })
export class PhoneAuthService {
	private auth = inject(Auth);
	private usersService = inject(UsersService);
	private analyticsService = inject(AnalyticsService);
	private toastAuthService = inject(AuthToastService);

	async requestVerificationCode(phoneNumber: IMobileNumber, recaptchaVerifier?: RecaptchaVerifier): Promise<string> {
		try {
			if (Capacitor.isNativePlatform()) {
				await FirebaseAuthentication.removeAllListeners();

				const verificationId = await new Promise<string>((resolve, reject) => {
					FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
						resolve(event.verificationId);
					});

					FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
						console.error('Phone verification failed:', event);
						reject(new Error(event.message || 'Phone verification failed.'));
					});

					FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber: phoneNumber.e164Number });
				});

				return verificationId;
			} else {
				const provider = new PhoneAuthProvider(this.auth);
				const verificationId = await provider.verifyPhoneNumber(phoneNumber.e164Number, recaptchaVerifier!);
				return verificationId;
			}
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Phone Number Verification Error', error.code);
			}
			throw error;
		}
	}
	async updatePhoneNumber(verificationId: string, verificationCode: string): Promise<void> {
		try {
			const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
			await updatePhoneNumber(this.auth.currentUser!, phoneCredential);
			this.analyticsService.logEvent(AnalyticEvent.PHONE_NUMBER_UPDATE);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Phone Number Update Error', error.code);
			}
			throw error;
		}
	}

	async signUpWithPhoneNumber(userSignUp: IUserPhoneSignUp, verificationId: string, verificationCode: string): Promise<void> {
		try {
			const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
			const result = await signInWithCredential(this.auth, phoneCredential);
			const user = result.user;

			const userCreate: IUser = {
				uid: user.uid,
				email: user.email,
				displayName: userSignUp.fullName,
				photoUrl: user.photoURL,
				phoneNumber: userSignUp.phoneNumber,
				settings: DEFAULT_USER_SETTINGS,
				createdAt: user.metadata.creationTime ? Timestamp.fromDate(new Date(user.metadata.creationTime)) : Timestamp.now(),
				updatedAt: Timestamp.now(),
			};

			await this.usersService.set(user.uid, userCreate, true);
			await updateProfile(result.user, { displayName: userSignUp.fullName });
			this.analyticsService.logEvent(AnalyticEvent.PHONE_NUMBER_SIGN_UP);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Phone Number Sign Up Error', error.code);
			}
			throw error;
		}
	}

	async signInWithPhoneNumber(verificationId: string, verificationCode: string): Promise<void> {
		try {
			const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
			await signInWithCredential(this.auth, phoneCredential);
			this.analyticsService.logEvent(AnalyticEvent.PHONE_NUMBER_SIGN_IN);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Phone Number Login Error', error.code);
			}
			throw error;
		}
	}
}
